import {
  AugmentedCreateTestamentaryEntityInput,
  AugmentedUpdateTestamentaryEntityInput,
  EntityGstStatus,
  TestamentaryEntityKind,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import {
  BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE,
  BENEFICIARIES_SUBFORM,
} from '../../EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { defaultBeneficiary } from '../../EntityBeneficiariesSubform/EntityBeneficiariesSubform.defaults';
import { EntityBeneficiariesFormBeneficiary } from '../../EntityBeneficiariesSubform/EntityBeneficiariesSubform.types';
import { makeCreateBeneficiaryInputs } from '../../EntitySubforms/utils/shared/beneficiaries.utils';
import { makeCreateControllingPartyInputs } from '../../EntitySubforms/utils/shared/controllingParties.utils';
import {
  entityGstStatusToApiTypeMap,
  estateInclusionStatusToApiTypeMap,
} from '../../EntitySubforms/utils/shared/taxStatus.utils';
import {
  makeCreateSuccessorTrusteeInputs,
  makeCreateTrustAdvisorInputs,
  makeCreateTrusteeInputs,
} from '../../EntitySubforms/utils/shared/trustees.utils';
import {
  IN_ESTATE,
  MIXED_GST,
  OUT_OF_ESTATE,
} from '../../TaxStatusSubform/TaxStatusSubform.constants';
import {
  defaultValues_TrusteesAndSuccessorTrustees,
  TRUST_DETAILS_SUBFORM_NAMESPACE,
} from '../../TrustDetailsSubform/TrustDetailsSubform.types';
import {
  CONTROLLING_PARTY_SUBFORM,
  defaultControllingParty,
} from './ControllingPartyInput';
import { TESTAMENTARY_ENTITY } from './testamentaryEntity.constants';
import {
  DefaultEstateTaxStatus,
  TestamentaryEntityFormShape,
} from './testamentaryEntity.types';

export function getCreateTestamentaryEntityInput(
  form: TestamentaryEntityFormShape,
  householdId: string
): AugmentedCreateTestamentaryEntityInput {
  const testamentaryEntityForm = form[TESTAMENTARY_ENTITY];
  const trustDetailsForm = form[TRUST_DETAILS_SUBFORM_NAMESPACE];
  const controllingPartiesSubform = form[CONTROLLING_PARTY_SUBFORM];
  const beneficiariesSubform = form[BENEFICIARIES_SUBFORM];
  const beneficiariesAfterBothGrantorDeathsSubform =
    form[BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE];

  if (
    !testamentaryEntityForm.displayName ||
    !testamentaryEntityForm.testamentaryEntityKind
  ) {
    throw new Error('missing required field for testamentary entity');
  }

  const {
    displayName,
    testamentaryEntityKind,
    _primaryClientDeathId,
    estateInclusionStatus,
    gstStatus,
    inclusionRatio,
    entityDetailsNotes,
    survivingSpouseStateInEstateStatus,
  } = testamentaryEntityForm;

  if (!householdId && !_primaryClientDeathId) {
    throw new Error('a household ID or primary client death ID is required');
  }

  const beneficiaries: EntityBeneficiariesFormBeneficiary[] =
    beneficiariesSubform.beneficiaries ?? [];
  const beneficiariesAfterBothDie: EntityBeneficiariesFormBeneficiary[] =
    beneficiariesAfterBothGrantorDeathsSubform?.beneficiaries ?? [];
  const withBeneficiaries = makeCreateBeneficiaryInputs(
    beneficiaries,
    householdId
  );
  const withBeneficiariesAfterBothGrantorDeaths =
    beneficiariesAfterBothDie.length > 0
      ? {
          withBeneficiariesAfterBothGrantorDeaths: makeCreateBeneficiaryInputs(
            beneficiariesAfterBothDie,
            householdId
          ),
        }
      : {};

  const output: AugmentedCreateTestamentaryEntityInput = {
    create: {
      displayName,
      kind: testamentaryEntityKind,
      grantorDeathID: _primaryClientDeathId,
      inEstateStatus: estateInclusionStatus
        ? estateInclusionStatusToApiTypeMap[estateInclusionStatus]
        : undefined,
      gstStatus: gstStatus ? entityGstStatusToApiTypeMap[gstStatus] : undefined,
      inclusionRatio:
        gstStatus === MIXED_GST && inclusionRatio ? inclusionRatio : undefined,
      description: entityDetailsNotes || undefined,
      householdID: householdId,
      survivingSpouseStateInEstateStatus: survivingSpouseStateInEstateStatus
        ? estateInclusionStatusToApiTypeMap[survivingSpouseStateInEstateStatus]
        : undefined,
    },
    withTrustees: makeCreateTrusteeInputs(trustDetailsForm, householdId),
    withSuccessorTrustees: makeCreateSuccessorTrusteeInputs(
      trustDetailsForm,
      householdId
    ),
    withTrustAdvisors: makeCreateTrustAdvisorInputs(
      trustDetailsForm,
      householdId
    ),
    withDispositiveProvisions: [],
    withControllingParties: controllingPartiesSubform
      ? makeCreateControllingPartyInputs(controllingPartiesSubform, householdId)
      : undefined,
    withBeneficiaries,
    ...withBeneficiariesAfterBothGrantorDeaths,
  };

  return output;
}

interface UpdateTestamentaryEntityInputArg {
  formValues: TestamentaryEntityFormShape;
  testamentaryEntityId: string;
  householdId: string;
}
export function getUpdateTestamentaryEntityInput({
  formValues,
  testamentaryEntityId,
  householdId,
}: UpdateTestamentaryEntityInputArg): AugmentedUpdateTestamentaryEntityInput {
  // Get the form values as if we were creating
  const createInput = getCreateTestamentaryEntityInput(formValues, householdId);
  const {
    gstStatus,
    inclusionRatio,
    inEstateStatus,
    survivingSpouseStateInEstateStatus,
    displayName,
    taxStatus,
    description,
    kind,
  } = createInput.create;

  const {
    withTrustees,
    withSuccessorTrustees,
    withTrustAdvisors,
    withControllingParties,
    withBeneficiaries,
    withBeneficiariesAfterBothGrantorDeaths,
  } = createInput;

  return {
    id: testamentaryEntityId,
    update: {
      clearTrustees: true,
      clearSuccessorTrustees: true,
      clearTrustAdvisors: true,
      clearControllingParties: true,
      clearDescription: !description,
      clearBeneficiaries: true,
      clearBeneficiariesAfterBothGrantorDeaths: true,
      clearGstStatus: !gstStatus,
      clearInclusionRatio:
        !inclusionRatio || gstStatus !== EntityGstStatus.MixedGst,
      gstStatus,
      inclusionRatio:
        gstStatus === EntityGstStatus.MixedGst && inclusionRatio
          ? inclusionRatio
          : undefined,
      inEstateStatus,
      taxStatus,
      displayName,
      survivingSpouseStateInEstateStatus,
      description: description || undefined,
      kind,
    },
    withTrustees,
    withSuccessorTrustees,
    withTrustAdvisors,
    withControllingParties,
    withBeneficiaries,
    withBeneficiariesAfterBothGrantorDeaths,
  };
}

export interface GetDefaultValuesParameter extends DefaultEstateTaxStatus {
  dyingPrimaryClientId: string | null;
  areAllClientsDead: boolean;
  householdId: string | null;
  defaultName?: string;
  defaultKind?: TestamentaryEntityKind;
}

export function getDefaultValues({
  dyingPrimaryClientId,
  areAllClientsDead,
  householdId,
  estateInclusionStatus: inputEstateInclusionStatus,
  survivingSpouseStateInEstateStatus: inputSurvivingSpouseStateInEstateStatus,
  gstStatus,
  inclusionRatio,
  defaultName,
  defaultKind,
}: GetDefaultValuesParameter): TestamentaryEntityFormShape {
  const beneficiariesAfterBothDie = areAllClientsDead
    ? {}
    : {
        [BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE]: {
          beneficiaries: [defaultBeneficiary()],
        },
      };

  const {
    estateInclusionStatus: defaultEstateInclusionStatus,
    survivingSpouseStateInEstateStatus:
      defaultSurvivingSpouseStateInEstateStatus,
  } = getTEAssetLocationDefaults(defaultKind, areAllClientsDead);

  return {
    [TESTAMENTARY_ENTITY]: {
      displayName: defaultName ?? '',
      testamentaryEntityKind: defaultKind ?? '',

      // Default the inEstate status based on the TE kind (unless defaults were provided)
      estateInclusionStatus:
        inputEstateInclusionStatus ?? defaultEstateInclusionStatus,
      survivingSpouseStateInEstateStatus:
        inputSurvivingSpouseStateInEstateStatus ??
        defaultSurvivingSpouseStateInEstateStatus,
      gstStatus: gstStatus ?? '',
      inclusionRatio: inclusionRatio ?? null,
      entityDetailsNotes: '',
      _primaryClientDeathId: dyingPrimaryClientId,
      householdId: householdId ?? '',
    },
    [CONTROLLING_PARTY_SUBFORM]: {
      controllingParties: [defaultControllingParty],
    },
    [TRUST_DETAILS_SUBFORM_NAMESPACE]:
      defaultValues_TrusteesAndSuccessorTrustees,
    [BENEFICIARIES_SUBFORM]: {
      beneficiaries: [defaultBeneficiary()],
    },
    ...beneficiariesAfterBothDie,
  };
}

/**
 * Return the default federal/state asset locations based on TE kind.
 * - GrantorEstate, InheritedRetirementAccount: In Estate
 * - CharitableEntity, CharitableTrust, TestamentaryTrust: Out of Estate
 *
 * SPECIAL CASE: single-grantor, or on 2nd death: Out of Estate
 *
 * @param testamentaryEntityKind
 * @param areAllClientsDead
 */
export function getTEAssetLocationDefaults(
  testamentaryEntityKind: TestamentaryEntityKind | undefined,
  areAllClientsDead: boolean
): Pick<
  TestamentaryEntityFormShape['testamentaryEntity'],
  'estateInclusionStatus' | 'survivingSpouseStateInEstateStatus'
> {
  if (areAllClientsDead) {
    return {
      estateInclusionStatus: OUT_OF_ESTATE,
      survivingSpouseStateInEstateStatus: OUT_OF_ESTATE,
    };
  }

  if (!testamentaryEntityKind) {
    // Leave the values blank until user selects a kind
    return {
      estateInclusionStatus: null,
      survivingSpouseStateInEstateStatus: null,
    };
  }

  switch (testamentaryEntityKind) {
    case TestamentaryEntityKind.GrantorEstate:
    case TestamentaryEntityKind.InheritedRetirementAccount:
      return {
        estateInclusionStatus: IN_ESTATE,
        survivingSpouseStateInEstateStatus: IN_ESTATE,
      };
    case TestamentaryEntityKind.CharitableEntity:
    case TestamentaryEntityKind.CharitableTrust:
    case TestamentaryEntityKind.TestamentaryTrust:
      return {
        estateInclusionStatus: OUT_OF_ESTATE,
        survivingSpouseStateInEstateStatus: OUT_OF_ESTATE,
      };
    default:
      throw new UnreachableError({
        case: testamentaryEntityKind,
        message: `Unknown testamentary entity kind ${testamentaryEntityKind} for getTEAssetLocationDefaults`,
      });
  }
}
